import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';
import SubscriptionService from '../../api/SubscriptionService';

const RegistrationFormPage = ({ history }) => {
  const [orgInfo, setOrgInfo] = useState(null);

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dni, setDni] = useState('');
  const [licenseType, setLicenseType] = useState('permiso_b');

  // UI states
  const [loading, setLoading] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  useEffect(() => {
    // Load organization info from session storage
    const storedOrgInfo = sessionStorage.getItem('registrationOrg');
    if (!storedOrgInfo) {
      // If no org info, redirect back to code entry
      history.push('/register');
      return;
    }

    setOrgInfo(JSON.parse(storedOrgInfo));
    // We're only using permiso_b type now
  }, [history]);

  // Check email availability as user types
  useEffect(() => {
    const checkEmailTimer = setTimeout(async () => {
      if (email && email.includes('@')) {
        setEmailCheckLoading(true);
        try {
          const response =
            await SubscriptionService.checkEmailAvailability(email);
          if (!response.available) {
            setFormErrors(prev => ({
              ...prev,
              email: 'Este email ya está registrado',
            }));
          } else {
            setFormErrors(prev => ({ ...prev, email: '' }));
          }
        } catch (err) {
          console.error('Failed to check email', err);
        } finally {
          setEmailCheckLoading(false);
        }
      }
    }, 800);

    return () => clearTimeout(checkEmailTimer);
  }, [email]);

  const validateForm = () => {
    const errors = {};

    if (!email) errors.email = 'El email es obligatorio';
    else if (!/\S+@\S+\.\S+/.test(email))
      errors.email = 'El email no es válido';

    if (!password) errors.password = 'La contraseña es obligatoria';
    else if (password.length < 8)
      errors.password = 'La contraseña debe tener al menos 8 caracteres';

    if (password !== confirmPassword)
      errors.confirmPassword = 'Las contraseñas no coinciden';

    if (!firstName) errors.firstName = 'El nombre es obligatorio';
    if (!lastName) errors.lastName = 'Los apellidos son obligatorios';
    if (!dni) errors.dni = 'El DNI/NIE es obligatorio';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setGeneralError(null);

    try {
      const userData = {
        friendly_code: orgInfo.code,
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        dni,
        license_type: licenseType,
      };

      const response = await SubscriptionService.registerUser(userData);

      if (response.success) {
        // Store registration success info for the next step
        sessionStorage.setItem(
          'registrationComplete',
          JSON.stringify({
            userId: response.user_id,
            organizationName: response.organization_name,
            subscriptionAmount: response.subscription_amount,
            email: email, // Store email for subscription setup
          })
        );

        // Redirect to success page
        history.push('/register/success');
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        'Error en el registro. Por favor, inténtelo de nuevo.';
      setGeneralError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    history.push('/register');
  };

  if (!orgInfo) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div
        className="w-full p-8 overflow-y-auto flex items-center justify-center flex-1 bg-cover bg-bottom"
        style={{ backgroundImage: 'url(/register.jpg)' }}
      >
        <div className="w-full md:max-w-md 2xl:max-w-xl">
          <div className="bg-white rounded-xl shadow-xl transform transition-all shadow-md hover:shadow-2xl focus:shadow-2xl">
            <div className="px-4 py-4 md:px-12 md:py-12">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 mb-6">
                <a
                  href="https://www.vrtus.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/logo_dark.jpg"
                    alt="Logo"
                    className="h-16 w-16 rounded-full object-cover cursor-pointer"
                  />
                </a>
              </div>

              <button
                onClick={goBack}
                className="mb-4 flex items-center text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft size={20} className="mr-1" />
                <span>Volver</span>
              </button>

              <div className="text-3xl font-medium text-gray-900 mb-2">
                Crea tu cuenta
              </div>

              <div className="text-gray-600 mb-6">
                Para {orgInfo.name}
                {orgInfo.subscriptionAmount && (
                  <span className="font-semibold ml-1">
                    ({orgInfo.subscriptionAmount}€/mes)
                  </span>
                )}
              </div>

              {generalError && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md mb-6">
                  {generalError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="flex flex-col mb-4">
                  <label className="text-gray-500 text-sm mb-1">Email *</label>
                  <input
                    type="email"
                    className={`rounded-md text-lg px-4 py-2 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="ejemplo@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={loading}
                  />
                  {formErrors.email && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.email}
                    </div>
                  )}
                  {emailCheckLoading && (
                    <div className="text-gray-500 text-sm mt-1">
                      Comprobando email...
                    </div>
                  )}
                </div>

                <div className="flex flex-col mb-4 relative">
                  <label className="text-gray-500 text-sm mb-1">
                    Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={isPasswordVisible ? 'text' : 'password'}
                      className={`rounded-md text-lg px-4 py-2 border ${formErrors.password ? 'border-red-500' : 'border-gray-300'} w-full pr-10`}
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                      tabIndex="-1"
                    >
                      {isPasswordVisible ? (
                        <Eye size={20} />
                      ) : (
                        <EyeSlash size={20} />
                      )}
                    </button>
                  </div>
                  {formErrors.password && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.password}
                    </div>
                  )}
                </div>

                <div className="flex flex-col mb-4 relative">
                  <label className="text-gray-500 text-sm mb-1">
                    Confirmar contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={isConfirmPasswordVisible ? 'text' : 'password'}
                      className={`rounded-md text-lg px-4 py-2 border ${formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'} w-full pr-10`}
                      placeholder="Repite tu contraseña"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                      tabIndex="-1"
                    >
                      {isConfirmPasswordVisible ? (
                        <Eye size={20} />
                      ) : (
                        <EyeSlash size={20} />
                      )}
                    </button>
                  </div>
                  {formErrors.confirmPassword && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.confirmPassword}
                    </div>
                  )}
                </div>

                {/* Changed from flex to separate fields to fix layout issue */}
                <div className="flex flex-col mb-4">
                  <label className="text-gray-500 text-sm mb-1">Nombre *</label>
                  <input
                    className={`rounded-md text-lg px-4 py-2 border ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Nombre"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    disabled={loading}
                  />
                  {formErrors.firstName && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.firstName}
                    </div>
                  )}
                </div>

                <div className="flex flex-col mb-4">
                  <label className="text-gray-500 text-sm mb-1">
                    Apellidos *
                  </label>
                  <input
                    className={`rounded-md text-lg px-4 py-2 border ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Apellidos"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    disabled={loading}
                  />
                  {formErrors.lastName && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.lastName}
                    </div>
                  )}
                </div>

                <div className="flex flex-col mb-4">
                  <label className="text-gray-500 text-sm mb-1">
                    DNI/NIE *
                  </label>
                  <input
                    className={`rounded-md text-lg px-4 py-2 border ${formErrors.dni ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="12345678A"
                    value={dni}
                    onChange={e => setDni(e.target.value)}
                    disabled={loading}
                  />
                  {formErrors.dni && (
                    <div className="text-red-500 text-sm mt-1">
                      {formErrors.dni}
                    </div>
                  )}
                </div>

                <div className="flex flex-col mb-6">
                  <label className="text-gray-500 text-sm mb-1">
                    Tipo de carnet
                  </label>
                  <select
                    className="rounded-md text-lg px-4 py-2 border border-gray-300"
                    value={licenseType}
                    onChange={e => setLicenseType(e.target.value)}
                    disabled={loading}
                  >
                    <option value="permiso_b">Permiso B (coche)</option>
                  </select>
                  <div className="text-gray-400 text-sm mt-1">
                    Selecciona el tipo de carnet que estás estudiando
                  </div>
                </div>

                <button
                  type="submit"
                  className="hover:bg-gray-600 font-medium rounded-lg text-lg px-4 py-2 bg-gray-500 text-white w-full"
                  disabled={loading || emailCheckLoading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                      <span>Procesando...</span>
                    </div>
                  ) : (
                    'Crear cuenta'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(RegistrationFormPage);
